import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">
              oopbuy<span className="text-primary-500">spread</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className="flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-4-line"></i>
              </span>
              Startseite
            </Link>
            <Link
              to="/oopbuy-spreadsheet-guide"
              className="flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-open-line"></i>
              </span>
              Shopping Guide
            </Link>
            <Link
              to="/spreadsheet-database"
              className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Database
            </Link>
            <Link
              to="/oopbuy-sicherheit"
              className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Sicherheit
            </Link>
            <a
              href="#kategorien"
              className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Kategorien
            </a>
          </div>

          <div className="flex items-center gap-3">
            <div ref={menuRef} className="md:hidden relative">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                  scrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-background-50 hover:bg-background-50/10'
                }`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
                </span>
              </button>

              {mobileMenuOpen && (
                <div className="absolute top-full right-0 mt-2 w-72 bg-background-50 border border-background-200/70 rounded-lg shadow-lg overflow-hidden z-50 max-h-[70vh] overflow-y-auto animate-fade-in-up">
                  <div className="p-3 border-b border-background-100 bg-background-50">
                    <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Navigation</span>
                  </div>
                  <Link
                    to="/"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer border-b border-background-100"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className="ri-home-4-line"></i>
                    </span>
                    Startseite
                  </Link>
                  <Link
                    to="/oopbuy-spreadsheet-guide"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer border-b border-background-100"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className="ri-book-open-line"></i>
                    </span>
                    Shopping Guide
                  </Link>
                  <Link
                    to="/spreadsheet-database"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer border-b border-background-100"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className="ri-database-2-line"></i>
                    </span>
                    Spreadsheet Database
                  </Link>
                  <Link
                    to="/oopbuy-sicherheit"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer border-b border-background-100"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className="ri-shield-check-line"></i>
                    </span>
                    Sicherheit
                  </Link>
                  <a
                    href="#kategorien"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer border-b border-background-100"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className="ri-grid-line"></i>
                    </span>
                    Kategorien
                  </a>
                  <div className="p-3 border-b border-background-100 bg-background-100/50">
                    <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Alle Kategorien</span>
                  </div>
                  {categories.map((cat, i) => (
                    <a
                      key={i}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-xs text-foreground-600 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer border-b border-background-100 last:border-b-0"
                    >
                      <span className="w-4 h-4 flex items-center justify-center text-primary-400 flex-shrink-0">
                        <i className={cat.icon}></i>
                      </span>
                      <span className="flex-1">{cat.name}</span>
                      <span className="w-3 h-3 flex items-center justify-center text-foreground-400">
                        <i className="ri-external-link-line text-xs"></i>
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}